import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { resolveBackendBaseUrl } from '../utils/runtimeConfig';

const cardStyles = 'rounded-xl border border-slate-700 bg-slate-900/70 p-4';

const NumberCard = ({ label, value }) => (
  <div className={cardStyles}>
    <p className="text-xs uppercase tracking-wide text-slate-400">{label}</p>
    <p className="mt-2 text-2xl font-semibold text-slate-100">{Number(value || 0).toLocaleString('id-ID')}</p>
  </div>
);

const DailyAuthChart = ({ rows }) => {
  const maxValue = useMemo(() => {
    const values = (rows || []).flatMap((item) => [Number(item?.success || 0), Number(item?.failed || 0)]);
    return Math.max(1, ...values);
  }, [rows]);

  return (
    <div className={cardStyles}>
      <h3 className="text-sm font-semibold text-slate-100">Daily Auth Success vs Failed</h3>
      <div className="mt-4 space-y-3">
        {(rows || []).map((item) => {
          const success = Number(item?.success || 0);
          const failed = Number(item?.failed || 0);
          const successPct = Math.min(100, Math.round((success / maxValue) * 100));
          const failedPct = Math.min(100, Math.round((failed / maxValue) * 100));
          return (
            <div key={item?.date} className="rounded-lg border border-slate-800 bg-slate-950/80 p-3">
              <p className="text-xs text-slate-400">{item?.date || '-'}</p>
              <div className="mt-2 space-y-1">
                <div className="flex items-center gap-2">
                  <span className="w-14 text-[11px] text-emerald-300">Success</span>
                  <div className="h-2 flex-1 overflow-hidden rounded bg-slate-800">
                    <div className="h-full bg-emerald-500" style={{ width: `${successPct}%` }} />
                  </div>
                  <span className="w-10 text-right text-xs text-emerald-300">{success}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-14 text-[11px] text-rose-300">Failed</span>
                  <div className="h-2 flex-1 overflow-hidden rounded bg-slate-800">
                    <div className="h-full bg-rose-500" style={{ width: `${failedPct}%` }} />
                  </div>
                  <span className="w-10 text-right text-xs text-rose-300">{failed}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const AdminNote = () => (
  <div className={cardStyles}>
    <h3 className="text-sm font-semibold text-slate-100">Admin Note: Metric Sources</h3>
    <ul className="mt-3 space-y-2 text-xs text-slate-300">
      <li>`active_subscribers` / `suspended_subscribers`: `internet_services.service_status` (billing source of truth).</li>
      <li>`failed_auth_today`: `service_logs` (`action_type=radius_sync`, `metadata.sync_status=failed`) hari ini.</li>
      <li>`active_tickets`: `customer_tickets.status` terbuka/assigned/in progress untuk customer yang terhubung service terfilter.</li>
      <li>`devices_down`: `nas_servers.is_online=false` (bisa difilter per NAS).</li>
      <li>Auth chart: agregasi harian `service_logs` radius sync success vs failed.</li>
    </ul>
  </div>
);

const NocPortal = () => {
  const [filters, setFilters] = useState({ area: '', nas_id: '', olt_name: '' });
  const [options, setOptions] = useState({ areas: [], nas_ids: [], olt_names: [] });
  const [summary, setSummary] = useState(null);
  const [authStats, setAuthStats] = useState({ items: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const buildHeaders = useCallback(() => {
    const token = localStorage.getItem('ktc_token') || localStorage.getItem('access_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const toQuery = useCallback((payload) => {
    const params = new URLSearchParams();
    Object.entries(payload || {}).forEach(([key, value]) => {
      if (String(value || '').trim()) params.set(key, String(value).trim());
    });
    return params.toString();
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const base = resolveBackendBaseUrl();
      const headers = buildHeaders();
      const query = toQuery(filters);
      const [optRes, summaryRes, authRes] = await Promise.all([
        fetch(`${base}/api/dashboard/filter-options`, { headers }),
        fetch(`${base}/api/dashboard/noc-summary${query ? `?${query}` : ''}`, { headers }),
        fetch(`${base}/api/dashboard/auth-stats?${toQuery({ ...filters, days: 7 })}`, { headers }),
      ]);
      if (!optRes.ok || !summaryRes.ok || !authRes.ok) {
        throw new Error('Gagal memuat dashboard NOC');
      }
      const [optJson, summaryJson, authJson] = await Promise.all([optRes.json(), summaryRes.json(), authRes.json()]);
      setOptions(optJson || { areas: [], nas_ids: [], olt_names: [] });
      setSummary(summaryJson || {});
      setAuthStats(authJson || { items: [] });
    } catch (err) {
      setError(String(err?.message || 'Gagal memuat data dashboard'));
    } finally {
      setLoading(false);
    }
  }, [buildHeaders, filters, toQuery]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  return (
    <main className="min-h-screen bg-slate-950 px-6 py-6 text-slate-100">
      <div className="mx-auto max-w-7xl">
        <header className="mb-5">
          <h1 className="text-xl font-semibold">NOC Dashboard</h1>
          <p className="mt-1 text-sm text-slate-400">Operational snapshot untuk subscriber auth, ticket aktif, dan health perangkat.</p>
        </header>

        <section className={`${cardStyles} mb-4`}>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
            <select
              className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm"
              value={filters.area}
              onChange={(e) => setFilters((prev) => ({ ...prev, area: e.target.value }))}
            >
              <option value="">Semua Area</option>
              {(options.areas || []).map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
            <select
              className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm"
              value={filters.nas_id}
              onChange={(e) => setFilters((prev) => ({ ...prev, nas_id: e.target.value }))}
            >
              <option value="">Semua NAS</option>
              {(options.nas_ids || []).map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
            <select
              className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm"
              value={filters.olt_name}
              onChange={(e) => setFilters((prev) => ({ ...prev, olt_name: e.target.value }))}
            >
              <option value="">Semua OLT</option>
              {(options.olt_names || []).map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
            <button
              type="button"
              onClick={loadAll}
              className="rounded-lg bg-slate-700 px-3 py-2 text-sm font-medium text-slate-100 hover:bg-slate-600"
            >
              {loading ? 'Memuat...' : 'Refresh'}
            </button>
          </div>
          {error ? <p className="mt-3 text-xs text-rose-300">{error}</p> : null}
        </section>

        <section className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
          <NumberCard label="Active Subscribers" value={summary?.active_subscribers} />
          <NumberCard label="Suspended Subscribers" value={summary?.suspended_subscribers} />
          <NumberCard label="Failed Auth Today" value={summary?.failed_auth_today} />
          <NumberCard label="Active Tickets" value={summary?.active_tickets} />
          <NumberCard label="Devices Down" value={summary?.devices_down} />
        </section>

        <section className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-2">
          <DailyAuthChart rows={authStats?.items || []} />
          <AdminNote />
        </section>
      </div>
    </main>
  );
};

export default NocPortal;
